import { UnreadReportsStorage } from '../documents/unread-reports-storage';
import { Observable, of } from 'rxjs';
import { Settings } from '../core';
import { HttpClient } from '@angular/common/http';
import { CategoriesStorage, ReportsPackage, Report, CategoryNaturalKey } from '../documents';
import { UserStorage } from '../user';
import { Injectable } from '@angular/core';
import { SponsorDocumentsCounter, SponsorDocumentsCounterReadModel } from '../documents/models/documents-counter';
import { publishReplay, refCount, concatMap, reduce, publishLast } from 'rxjs/operators';

@Injectable()
export class SponsorUnreadReportsStorage extends UnreadReportsStorage<SponsorDocumentsCounter> {
    constructor(
        settings: Settings,
        http: HttpClient,
        categoriesStorage: CategoriesStorage,
        userStorage: UserStorage
    ) {
        super(settings, http, categoriesStorage, userStorage);
    }

    public getAlertsUnreadBySitesCounter(): Observable<SponsorDocumentsCounter[]> {
        const allCounters$ = this.loadUnreadCounters(`sponsor/documents-counters/result-alerts/sites`, SponsorDocumentsCounterReadModel).pipe(
            publishLast(),
            refCount()
        );

        return allCounters$;
    }

    protected getUnreadCounters(): Observable<SponsorDocumentsCounter[]> {
        const allCounters$ = of<CategoryNaturalKey[]>(['result-alerts', 'sponsor-communications']).pipe(
            concatMap(categoryIds => categoryIds.map(id => this.loadUnreadCounters(`sponsor/documents-counters/${id}`, SponsorDocumentsCounterReadModel))),
            concatMap(x => x),
            reduce(
                (result, counters) => {
                    result.push(...counters);
                    return result;
                },
                [] as SponsorDocumentsCounter[]
            ),
            publishReplay(1),
            refCount()
        );

        return allCounters$;
    }

    protected recountOnDocumentsReading(updatedReports: ReportsPackage, unreadCounters: SponsorDocumentsCounter[]): SponsorDocumentsCounter[] {
        const recentlyReadReports: Report[] = updatedReports.reports;
        const currentCategoryId: CategoryNaturalKey = updatedReports.category.categoryId;

        const item = unreadCounters.find(c =>
            (c.rootCategoryId === currentCategoryId || c.categoryId === currentCategoryId)
            && c.protocolId === recentlyReadReports[0].protocolId
        );

        if (item) {
            item.unreadCount -= recentlyReadReports.length;
        }

        return unreadCounters;
    }

    protected recountOnCountersUpdating(updatedReports: ReportsPackage, unreadCounters: SponsorDocumentsCounter[]): SponsorDocumentsCounter[] {
        const unreadReports: Report[] = updatedReports.reports.filter(report => !report.isRead);
        const currentCategoryId: CategoryNaturalKey = updatedReports.category.categoryId;

        if (unreadReports.length === 0 || unreadCounters.length === 0) {
            return unreadCounters;
        }

        const item = unreadCounters.find(c =>
            (c.rootCategoryId === currentCategoryId || c.categoryId === currentCategoryId)
            && c.protocolId === unreadReports[0].protocolId
        );

        if (item) {
            item.unreadCount = unreadReports.length;
        } else {
            const unreadData = new SponsorDocumentsCounter();
            unreadData.protocolId = unreadReports[0].protocolId;
            unreadData.categoryId = currentCategoryId;
            unreadData.unreadCount = unreadReports.length;
            unreadData.totalDocs = updatedReports.reports.length;
            unreadCounters.push(unreadData);
        }

        return unreadCounters;
    }
}